import {
    Checkbox,
    Chip,
    FormControlLabel,
    TextField,
    Tooltip,
} from "@mui/material";
import Autocomplete, { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";
import React from "react";
import {AutoCompleteMultipleProps} from "./AutoCompleteProps";

const AutoCompleteMultiple = ({
    id,
    options,
    label,
    disabled,
    value,
    setValue,
    maxToSelect = 10,
    formatOptionLabel,
    formatTagLabel,
    getOptionDisabled,
    isOptionGroup,
}: AutoCompleteMultipleProps) => {
    const selectedValues = value ?? [];
    const renderLabel = (option: string) =>
        formatOptionLabel ? formatOptionLabel(option) : option;
    const renderTagLabel = (option: string) =>
        formatTagLabel ? formatTagLabel(option) : option;
    const optionIsGroup = (option: string) =>
        isOptionGroup ? isOptionGroup(option) : false;
    const isDisabledOption = (option: string) =>
        getOptionDisabled ? getOptionDisabled(option) : false;

    return (
        <Autocomplete
            id={id}
            multiple
            disableCloseOnSelect
            limitTags={1}
            size="small"
            fullWidth
            options={options}
            value={selectedValues}
            disabled={disabled}
            getOptionDisabled={(option: string) => isDisabledOption(option)}
            onChange={(_event: React.SyntheticEvent, newValue: string[]) => {
                if (maxToSelect === 1) {
                    setValue(newValue.length ? [newValue[newValue.length - 1]] : []);
                    return;
                }
                setValue(newValue.slice(0, maxToSelect));
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string, state: AutocompleteRenderOptionState) => {
                const selectedCount = selectedValues.length;
                const remaining = Math.max(maxToSelect - selectedCount, 0);
                const atLimit = selectedCount >= maxToSelect && !state.selected;
                const limitReplacesSelection = maxToSelect === 1 && atLimit;
                const disableForLimit = atLimit && maxToSelect !== 1;
                const disabledOption = isDisabledOption(option) || disableForLimit;
                const labelNode = optionIsGroup(option) ? (
                    <span className="autocompleteGroupLabel">
                        {renderLabel(option)}
                    </span>
                ) : (
                    renderLabel(option)
                );

                return (
                    <li {...props}>
                        <Tooltip
                            title={
                                limitReplacesSelection
                                    ? "Click to replace the current selection"
                                    : `${remaining < 1 ? "No" : remaining} more selection${remaining === 1 ? "" : "s"} available`
                            }
                        >
                            <FormControlLabel
                                disabled={disabledOption}
                                control={<Checkbox checked={state.selected} />}
                                label={labelNode}
                                style={{
                                    width: "100%",
                                    margin: 0,
                                    opacity: limitReplacesSelection ? 0.5 : 1,
                                }}
                            />
                        </Tooltip>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={`${label} ${selectedValues.length}/${maxToSelect}`}
                    variant="outlined"
                />
            )}
            renderTags={(tagValue: string[], getTagProps) =>
                tagValue.map((option: string, index: number) => (
                    <Chip
                        {...getTagProps({index})}
                        label={renderTagLabel(option)}
                        size="small"
                    />
                ))
            }
        />
    );
};

export default AutoCompleteMultiple;
